import BackButton from '../components/BackButton';
import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function AdminAnalytics() {
  const { isAdmin } = useApp();
  const { t } = useI18n();
  const [overview, setOverview] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (isAdmin) loadAnalytics();
  }, [isAdmin, days]);

  async function loadAnalytics() {
    setLoading(true);
    try {
      const [ov, rev, tr] = await Promise.all([
        api.get(`/analytics/admin/overview?days=${days}`),
        api.get(`/analytics/admin/revenue?days=${days}`),
        api.get(`/analytics/admin/trends?days=${Math.min(days, 30)}`),
      ]);
      setOverview(ov.data);
      setRevenue(rev.data);
      setTrends(tr.data);
    } catch (e) {
      console.error('Analytics error:', e);
    } finally {
      setLoading(false);
    }
  }

  if (!isAdmin) {
    return <div className="page"><BackButton /><h2>⛔ Доступ запрещён</h2></div>;
  }

  const StatCard = ({ icon, label, value, sub, color = '108,92,231' }) => (
    <div style={{
      background: `rgba(${color},0.15)`,
      borderRadius: 12,
      padding: 16,
      textAlign: 'center',
      flex: 1,
      minWidth: 100,
    }}>
      <div style={{ fontSize: 28 }}>{icon}</div>
      <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, opacity: 0.5 }}>{sub}</div>}
    </div>
  );

  const RevenueCard = ({ title, value, details }) => (
    <div style={{
      background: 'rgba(0,206,201,0.15)',
      borderRadius: 12,
      padding: 16,
    }}>
      <div style={{ fontSize: 14, opacity: 0.7 }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#00cec9' }}>
        {value.toLocaleString()} PLN
      </div>
      {details && <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>{details}</div>}
    </div>
  );

  // Simple bar chart
  const MiniChart = ({ data, dataKey, color = '#667eea' }) => {
    if (!data?.length) return null;
    const max = Math.max(...data.map(d => d[dataKey] || 0), 1);
    
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 60 }}>
        {data.slice(-14).map((d, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: `${((d[dataKey] || 0) / max) * 100}%`,
              minHeight: 2,
              background: color,
              borderRadius: '2px 2px 0 0',
              opacity: 0.7 + (i / data.length) * 0.3,
            }}
            title={`${d.date}: ${d[dataKey]}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="page"><BackButton />
      <h1>📊 Analytics Dashboard</h1>

      {/* Period selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[7, 30, 90, 365].map(d => (
          <button
            key={d}
            className={`btn btn-small ${days === d ? 'active' : ''}`}
            onClick={() => setDays(d)}
            style={{
              background: days === d ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
              border: 'none',
              padding: '8px 16px',
              borderRadius: 20,
            }}
          >
            {d === 365 ? '1 год' : `${d} дн`}
          </button>
        ))}
        <button className="btn btn-small" onClick={loadAnalytics}>🔄</button>
      </div>

      {loading ? (
        <div className="loader">{t('common.loading')}</div>
      ) : (
        <>
          {/* Overview */}
          {overview && (
            <div style={{ marginBottom: 24 }}>
              <h3>👥 Пользователи</h3>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <StatCard icon="👥" label="Всего" value={overview.users.total} />
                <StatCard icon="🆕" label="Новых" value={overview.users.new} sub={`за ${days} дн`} />
                <StatCard icon="✅" label="Верифиц." value={overview.users.verified} sub={`${overview.users.verification_rate}%`} />
              </div>

              <h3>📝 Контракты</h3>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <StatCard icon="🚀" label="Gateway" value={overview.gateway_contracts.active} sub="активных" color="0,206,201" />
                <StatCard icon="📄" label="Work" value={overview.work_contracts.total} color="108,92,231" />
                <StatCard icon="💸" label="Payouts" value={overview.payouts.pending} sub="pending" color="255,193,7" />
              </div>
            </div>
          )}

          {/* Revenue */}
          {revenue && (
            <div style={{ marginBottom: 24 }}>
              <h3>💰 Доходы за {days} дней</h3>
              <div style={{
                background: 'linear-gradient(135deg, rgba(0,206,201,0.2), rgba(108,92,231,0.2))',
                borderRadius: 16,
                padding: 20,
                marginBottom: 16,
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 14, opacity: 0.7 }}>Общий доход</div>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#00cec9' }}>
                  {revenue.total_revenue_pln.toLocaleString()} PLN
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                <RevenueCard 
                  title="Gateway (1200 PLN)" 
                  value={revenue.gateway_fees.revenue_pln}
                  details={`${revenue.gateway_fees.count} новых`}
                />
                <RevenueCard 
                  title="Абонемент (350 PLN/мес)" 
                  value={revenue.monthly_fees.revenue_pln}
                  details={`${revenue.monthly_fees.active_contracts} активных`}
                />
                <RevenueCard 
                  title="Комиссия (6%)" 
                  value={revenue.commissions.revenue_pln}
                  details={`от ${revenue.commissions.payout_volume_pln.toLocaleString()} PLN`}
                />
              </div>
            </div>
          )}

          {/* Trends */}
          {trends?.trends && (
            <div style={{ marginBottom: 24 }}>
              <h3>📈 Тренды</h3>
              
              <div style={{ 
                background: 'rgba(0,0,0,0.2)', 
                borderRadius: 12, 
                padding: 16,
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 13, marginBottom: 8, opacity: 0.7 }}>
                  🆕 Новые пользователи (последние 14 дней)
                </div>
                <MiniChart data={trends.trends} dataKey="new_users" color="#667eea" />
              </div>

              <div style={{ 
                background: 'rgba(0,0,0,0.2)', 
                borderRadius: 12, 
                padding: 16,
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 13, marginBottom: 8, opacity: 0.7 }}>
                  🚀 Новые Gateway контракты
                </div>
                <MiniChart data={trends.trends} dataKey="new_gateways" color="#00cec9" />
              </div>

              <div style={{ 
                background: 'rgba(0,0,0,0.2)', 
                borderRadius: 12, 
                padding: 16,
              }}>
                <div style={{ fontSize: 13, marginBottom: 8, opacity: 0.7 }}>
                  💰 Объём выплат (PLN)
                </div>
                <MiniChart data={trends.trends} dataKey="payout_volume" color="#fdcb6e" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
